import { NavLink, useNavigate, useLocation, Outlet } from "react-router-dom";
import {
  LayoutDashboard,
  Home,
  FileText,
  Receipt,
  Users,
  Settings as SettingsIcon,
  LogOut,
  BookOpen,
  BarChart3,
  Briefcase,
  Truck,
  Recycle,
  FolderOpen,
  StickyNote,
  Menu,
  ChevronDown,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { canVisitPath } from "@/lib/permissions";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "@/components/ui/sheet";

const HOME = { to: "/", label: "Home", icon: Home, testid: "nav-hub", end: true };

const WASTE_GROUP = {
  group: "/waste-notes", label: "Waste transfer", icon: Recycle, testid: "nav-waste-toggle",
  children: [
    { to: "/waste-notes", label: "All waste notes", icon: Recycle, testid: "nav-waste-notes", end: true },
    { to: "/waste-notes/new", label: "New waste note", icon: Recycle, testid: "nav-waste-notes-new" },
    { to: "/waste-notes/completed", label: "Completed waste notes", icon: BookOpen, testid: "nav-waste-notes-completed", end: true },
    { to: "/waste-notes/register", label: "Waste register", icon: BookOpen, testid: "nav-waste-notes-register", end: true },
  ],
};

const linkCls = (active, sub) => `flex items-center gap-3 px-3 py-2.5 text-sm transition-colors rounded-sm ${sub ? "pl-9" : ""} ${
  active ? "bg-slate-800 text-white" : "text-slate-400 hover:bg-slate-800/60 hover:text-white"}`;

// Collapsible sidebar group; opens automatically when you're on one of its pages.
function NavGroup({ item, Wrap = ({ children }) => children }) {
  const location = useLocation();
  const inside = location.pathname.startsWith(item.group);
  const [open, setOpen] = useState(inside);
  useEffect(() => { if (inside) setOpen(true); }, [inside]);
  return (
    <div>
      <button type="button" data-testid={item.testid} onClick={() => setOpen((o) => !o)} aria-expanded={open}
        className={`w-full ${linkCls(inside && !open, false)}`}>
        <item.icon className="h-4 w-4" />
        <span className="font-medium flex-1 text-left">{item.label}</span>
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-1 space-y-1" data-testid={`${item.testid}-children`}>
          {item.children.map((c) => (
            <Wrap key={c.to}>
              <NavLink to={c.to} end={c.end} data-testid={c.testid} className={({ isActive }) => linkCls(isActive, true)}>
                <c.icon className="h-4 w-4" />
                <span className="font-medium">{c.label}</span>
              </NavLink>
            </Wrap>
          ))}
        </div>
      )}
    </div>
  );
}

// Each module shows its own sidebar entries. Home is always added at the top.
const SECTIONS = {
  jobs: {
    title: "Jobs",
    items: [
      { to: "/jobs", label: "All jobs", icon: Briefcase, testid: "nav-jobs", end: true },
      { to: "/jobs/new", label: "New job", icon: Briefcase, testid: "nav-jobs-new" },
      WASTE_GROUP,
      { to: "/jobs/charges", label: "Charges", icon: Receipt, testid: "nav-jobs-charges" },
    ],
  },
  "waste-notes": {
    title: "Jobs",
    items: [
      { to: "/jobs", label: "All jobs", icon: Briefcase, testid: "nav-jobs", end: true },
      { to: "/jobs/new", label: "New job", icon: Briefcase, testid: "nav-jobs-new" },
      WASTE_GROUP,
      { to: "/jobs/charges", label: "Charges", icon: Receipt, testid: "nav-jobs-charges" },
    ],
  },
  "purchase-orders": {
    title: "Purchase Orders & Self-Bills",
    noHome: true,
    items: [
      { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-hub", end: true },
      { to: "/purchase-orders", label: "Purchase Orders", icon: FileText, testid: "nav-pos", end: true },
      { to: "/purchase-orders/register", label: "PO Register", icon: BookOpen, testid: "nav-po-register" },
      { to: "/self-bills", label: "Self-Bills", icon: Receipt, testid: "nav-sbs", end: true },
      { to: "/self-bills/register", label: "Self-Bill Register", icon: BookOpen, testid: "nav-register" },
    ],
  },
  "self-bills": {
    title: "Purchase Orders & Self-Bills",
    noHome: true,
    items: [
      { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-hub", end: true },
      { to: "/purchase-orders", label: "Purchase Orders", icon: FileText, testid: "nav-pos", end: true },
      { to: "/purchase-orders/register", label: "PO Register", icon: BookOpen, testid: "nav-po-register" },
      { to: "/self-bills", label: "Self-Bills", icon: Receipt, testid: "nav-sbs", end: true },
      { to: "/self-bills/register", label: "Self-Bill Register", icon: BookOpen, testid: "nav-register" },
    ],
  },
  customers: {
    title: "Clients",
    noHome: true,
    items: [
      { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-hub", end: true },
      { to: "/clients", label: "Clients", icon: Users, testid: "nav-clients", end: true },
    ],
  },
  clients: {
    title: "Clients",
    noHome: true,
    items: [
      { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-hub", end: true },
      { to: "/clients", label: "Clients", icon: Users, testid: "nav-clients", end: true },
    ],
  },
  suppliers: {
    title: "Subcontractors",
    noHome: true,
    items: [
      { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-hub", end: true },
      { to: "/contacts?tab=subs", label: "Subcontractor", icon: Truck, testid: "nav-subcontractor" },
      { to: "/contacts?tab=drivers", label: "Self-employed", icon: Users, testid: "nav-selfemployed" },
    ],
  },
  contacts: {
    title: "Subcontractors",
    noHome: true,
    items: [
      { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-hub", end: true },
      { to: "/contacts?tab=subs", label: "Subcontractor", icon: Truck, testid: "nav-subcontractor" },
      { to: "/contacts?tab=drivers", label: "Self-employed", icon: Users, testid: "nav-selfemployed" },
    ],
  },
  reports: {
    title: "Reports",
    items: [
      { to: "/reports", label: "Monthly summary", icon: BarChart3, testid: "nav-reports", end: true },
      { to: "/dashboard", label: "Overview", icon: LayoutDashboard, testid: "nav-dashboard" },
      { to: "/reports/profiles", label: "Profiles", icon: BookOpen, testid: "nav-reports-profiles", end: true },
      { to: "/reports/jobs", label: "Job reports", icon: BarChart3, testid: "nav-reports-jobs", end: true },
      { to: "/reports/client-summary", label: "Client report", icon: BookOpen, testid: "nav-reports-client-summary", end: true },
      { to: "/reports/vehicles", label: "D4PS Vehicle reports", icon: Truck, testid: "nav-reports-vehicles", end: true },
      { to: "/reports/subcontractor-invoices", label: "Subcontractor invoice reports", icon: Receipt, testid: "nav-reports-sub-invoices", end: true },
    ],
  },
  dashboard: {
    title: "Overview",
    items: [
      { to: "/dashboard", label: "Overview", icon: LayoutDashboard, testid: "nav-dashboard", end: true },
      { to: "/reports", label: "Monthly summary", icon: BarChart3, testid: "nav-reports" },
    ],
  },
  settings: {
    title: "Settings",
    items: [
      { to: "/settings", label: "Company settings", icon: SettingsIcon, testid: "nav-settings", end: true },
      { to: "/settings/users", label: "Users", icon: Users, testid: "nav-settings-users", end: true },
    ],
  },
  documents: { title: "Documents", items: [{ to: "/documents", label: "Documents", icon: BookOpen, testid: "nav-documents", end: true }] },
  finance: {
    title: "Finance",
    noHome: true,
    items: [
      { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-hub", end: true },
      { to: "/finance/chargeable", label: "Chargeable", icon: BarChart3, testid: "nav-finance-chargeable", end: true },
      { to: "/finance/payable", label: "Payable", icon: Truck, testid: "nav-finance-payable", end: true },
      { to: "/finance/profit-reports", label: "Profit reports", icon: BarChart3, testid: "nav-finance-profit-reports", end: true },
    ],
  },
  vehicles: {
    title: "D4PS Vehicles",
    noHome: true,
    items: [
      { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-hub", end: true },
      { to: "/vehicles", label: "Fleet register", icon: Truck, testid: "nav-vehicles", end: true },
      { to: "/vehicles/maintenance", label: "Maintenance records", icon: BookOpen, testid: "nav-vehicles-maintenance", end: true },
      { to: "/vehicles/maintenance/suppliers", label: "Maintenance suppliers", icon: BookOpen, testid: "nav-vehicles-maint-suppliers", end: true },
      { to: "/vehicles/incidents", label: "Incidents & accidents", icon: BookOpen, testid: "nav-vehicles-incidents", end: true },
    ],
  },
  "subcontractor-invoices": {
    title: "Subcontractor Invoices",
    noHome: true,
    items: [
      { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-hub", end: true },
      { to: "/purchase-orders", label: "Purchase Orders", icon: FileText, testid: "nav-pos", end: true },
      { to: "/purchase-orders/register", label: "PO Register", icon: BookOpen, testid: "nav-po-register" },
      { to: "/subcontractor-invoices", label: "Subcontractor invoices", icon: Receipt, testid: "nav-sub-invoices", end: true },
    ],
  },
  "d4ps-documents": {
    title: "D4PS Certifications",
    noHome: true,
    items: [
      { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-hub", end: true },
      { to: "/d4ps-documents", label: "All certifications", icon: FolderOpen, testid: "nav-d4ps-docs", end: true },
    ],
  },
  "d4ps-insurances": {
    title: "D4PS Insurances",
    noHome: true,
    items: [
      { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-hub", end: true },
      { to: "/d4ps-insurances", label: "All insurances", icon: FolderOpen, testid: "nav-d4ps-insurances", end: true },
    ],
  },
  notes: {
    title: "Notes",
    noHome: true,
    items: [
      { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-hub", end: true },
      { to: "/notes", label: "All notes", icon: StickyNote, testid: "nav-notes", end: true },
    ],
  },
  hr: {
    title: "HR",
    noHome: true,
    items: [
      { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-hub", end: true },
      { to: "/hr", label: "Drivers", icon: Users, testid: "nav-hr-drivers" },
    ],
  },
  policies: {
    title: "Policies",
    noHome: true,
    items: [
      { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-hub", end: true },
      { to: "/policies", label: "All policies", icon: FileText, testid: "nav-policies" },
    ],
  },
  procedures: {
    title: "Procedures",
    noHome: true,
    items: [
      { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-hub", end: true },
      { to: "/procedures", label: "All procedures", icon: FileText, testid: "nav-procedures" },
    ],
  },
};

function sectionForPath(pathname) {
  const seg = pathname.split("/").filter(Boolean)[0] || "";
  return SECTIONS[seg] || null;
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const section = sectionForPath(location.pathname);
  const navItemsRaw = section
    ? (section.noHome ? section.items : [HOME, ...section.items])
    : [HOME];
  // Restrict Users management to the owner account only, and hide any sidebar
  // link that points to a module the user isn't allowed to visit.
  const allowed = (it) => {
    if (it.to === "/settings/users" && (user?.email || "").toLowerCase() !== "info@d4pslogistics.com") {
      return false;
    }
    // Always allow the home / hub link.
    if (it.to === "/" || it.to === HOME.to) return true;
    // Strip query strings before checking (e.g. /contacts?tab=subs → /contacts).
    const path = it.to.split("?")[0];
    return canVisitPath(user, path);
  };
  const navItems = navItemsRaw
    .map((it) => (it.children ? { ...it, children: it.children.filter(allowed) } : it))
    .filter((it) => (it.children ? it.children.length > 0 : allowed(it)));

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Sidebar */}
      <aside
        className="hidden md:flex md:w-64 lg:w-72 shrink-0 flex-col bg-slate-900 text-slate-200"
        data-testid="app-sidebar"
      >
        <div className="px-6 py-7 border-b border-slate-800">
          <Logo variant="light" size="md" />
        </div>
        <nav className="flex-1 px-3 py-6 space-y-1">
          {section && (
            <div
              className="px-3 pb-2 pt-1 text-[11px] uppercase tracking-wider text-slate-500"
              data-testid="sidebar-section-title"
            >
              {section.title}
            </div>
          )}
          {navItems.map((item) => {
            if (item.children) return <NavGroup key={item.group} item={item} />;
            // For items with a query string, treat active when pathname + ?tab match.
            const [itemPath, itemQuery] = item.to.split("?");
            const itemTab = itemQuery ? new URLSearchParams(itemQuery).get("tab") : null;
            const currentTab = new URLSearchParams(location.search).get("tab");
            const pathMatch = item.end
              ? location.pathname === itemPath
              : location.pathname.startsWith(itemPath);
            const queryActive = itemTab ? pathMatch && currentTab === itemTab : pathMatch && (!itemTab || !currentTab);
            const overrideActive = itemTab ? queryActive : null;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={item.testid}
                className={({ isActive }) => {
                  const active = overrideActive !== null ? overrideActive : isActive;
                  return `flex items-center gap-3 px-3 py-2.5 text-sm transition-colors rounded-sm ${
                    active
                      ? "bg-slate-800 text-white"
                      : "text-slate-400 hover:bg-slate-800/60 hover:text-white"
                  }`;
                }}
              >
                <item.icon className="h-4 w-4" />
                <span className="font-medium">{item.label}</span>
              </NavLink>
            );
          })}
        </nav>
        <div className="px-4 py-5 border-t border-slate-800">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-9 w-9 rounded-sm bg-slate-700 flex items-center justify-center text-sm font-semibold text-white">
              {(user?.name || "A").slice(0, 1).toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-medium text-white truncate">{user?.name || "Admin"}</div>
              <div className="text-xs text-slate-400 truncate">{user?.email}</div>
            </div>
          </div>
          <Button
            data-testid="logout-button"
            variant="outline"
            className="w-full bg-transparent border-slate-700 text-slate-200 hover:bg-slate-800 hover:text-white rounded-sm"
            onClick={handleLogout}
          >
            <LogOut className="h-4 w-4 mr-2" />
            Sign out
          </Button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-30 bg-slate-900 text-white px-4 py-3 flex items-center gap-3" data-testid="mobile-topbar">
        <Sheet>
          <SheetTrigger asChild>
            <button
              data-testid="mobile-menu-btn"
              aria-label="Open navigation"
              className="h-9 w-9 inline-flex items-center justify-center rounded-sm hover:bg-slate-800"
            >
              <Menu className="h-5 w-5" />
            </button>
          </SheetTrigger>
          <SheetContent side="left" className="w-72 bg-slate-900 text-slate-200 border-r border-slate-800 p-0 flex flex-col">
            <div className="px-6 py-6 border-b border-slate-800">
              <Logo variant="light" size="md" />
            </div>
            <nav className="flex-1 px-3 py-5 space-y-1 overflow-y-auto">
              {section && (
                <div className="px-3 pb-2 pt-1 text-[11px] uppercase tracking-wider text-slate-500">
                  {section.title}
                </div>
              )}
              {navItems.map((item) => {
                if (item.children) return <NavGroup key={item.group} item={item} Wrap={({ children }) => <SheetClose asChild>{children}</SheetClose>} />;
                const Icon = item.icon;
                return (
                  <SheetClose asChild key={item.to}>
                    <NavLink
                      to={item.to}
                      end={item.end}
                      data-testid={item.testid}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition ${
                          isActive ? "bg-slate-800 text-white" : "text-slate-300 hover:bg-slate-800 hover:text-white"
                        }`
                      }
                    >
                      <Icon className="h-4 w-4 shrink-0" />
                      <span className="font-medium">{item.label}</span>
                    </NavLink>
                  </SheetClose>
                );
              })}
            </nav>
            <div className="px-4 py-4 border-t border-slate-800">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-9 w-9 rounded-sm bg-slate-700 flex items-center justify-center text-sm font-semibold text-white">
                  {(user?.name || "A").slice(0, 1).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-white truncate">{user?.name || "Admin"}</div>
                  <div className="text-xs text-slate-400 truncate">{user?.email}</div>
                </div>
              </div>
              <Button
                variant="outline"
                className="w-full bg-transparent border-slate-700 text-slate-200 hover:bg-slate-800 hover:text-white rounded-sm"
                onClick={handleLogout}
              >
                <LogOut className="h-4 w-4 mr-2" />
                Sign out
              </Button>
            </div>
          </SheetContent>
        </Sheet>
        <div className="flex-1 min-w-0">
          <Logo variant="light" size="sm" />
        </div>
        {section && (
          <span className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-semibold truncate max-w-[120px]">{section.title}</span>
        )}
      </div>

      <main className="flex-1 min-w-0 pt-14 md:pt-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
